/**
 * Wrapper HTTP du module Revision Cards. Trois endpoints :
 *  - `GET /api/v1/training/cards/due` (liste filtrable par module)
 *  - `GET /api/v1/training/cards/stats` (KPI strip)
 *  - `POST /api/v1/training/cards/{id}/review` (auto-évaluation Léon)
 *
 * Cf. plan `docs/plans/2026-05-20-revision-cards-module.md` §3 pour le
 * contrat complet (champs, codes erreur).
 */

import { http } from '@/api/http';
import type { Outcome } from '@/api/outcome';
import type {
  CardStats,
  DueCardsResponse,
  RevisionModule,
  ReviewResponse,
} from './types';

/**
 * Récupère les cartes dues (et imminentes — laissé au backend). Quand
 * `module` est fourni, filtre côté serveur. `limit` est utile pour la
 * preview Mission Control ; sans valeur, le backend renvoie son default
 * (50 selon le plan, plafonné 200).
 */
export function fetchDueCards(
  module?: RevisionModule,
  limit?: number,
): Promise<DueCardsResponse> {
  const qs = new URLSearchParams();
  if (module) qs.set('module', module);
  if (limit !== undefined) qs.set('limit', String(limit));
  const suffix = qs.toString();
  return http.get<DueCardsResponse>(
    `/api/v1/training/cards/due${suffix ? `?${suffix}` : ''}`,
  );
}

export function fetchCardStats(): Promise<CardStats> {
  return http.get<CardStats>('/api/v1/training/cards/stats');
}

/**
 * POST review : Léon a auto-évalué la carte. `outcome` est l'un des 5 états
 * MVP2.4 T9.1 (cf. `@/api/outcome`) — la conversion succès/échec se fait
 * côté backend (succès = advance, échec = reboost).
 */
export function postReview(
  cardId: number,
  outcome: Outcome,
  timeSpentMs: number,
): Promise<ReviewResponse> {
  return http.post<ReviewResponse>(`/api/v1/training/cards/${cardId}/review`, {
    outcome,
    time_spent_ms: timeSpentMs,
  });
}

export const revisionCardsApi = {
  fetchDueCards,
  fetchCardStats,
  postReview,
};
